import React from 'react';

/**
 * ProgressBar - Visual progress indicator with optional warning threshold
 * Used for license utilization tracking
 *
 * @param {number} current - Current value
 * @param {number} total - Total/max value
 * @param {number} warningThreshold - Percentage threshold for warning color (default 90)
 * @param {boolean} showLabel - Show percentage label (default true)
 * @param {string} label - Custom label text
 */
const ProgressBar = ({
  current,
  total,
  warningThreshold = 90,
  showLabel = true,
  label
}) => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;
  const isWarning = percentage >= warningThreshold;

  const getBarColor = () => {
    if (percentage >= 95) return 'bg-red-600';
    if (isWarning) return 'bg-yellow-500';
    return 'bg-indigo-600';
  };

  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">
            {label || `${current} / ${total}`}
          </span>
          <span className={`text-sm font-semibold ${
            isWarning ? 'text-yellow-600' : 'text-gray-600'
          }`}>
            {percentage}%
          </span>
        </div>
      )}
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div
          className={`h-3 rounded-full transition-all duration-300 ${getBarColor()}`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      {isWarning && percentage < 100 && (
        <p className="mt-1 text-xs text-yellow-600">
          ⚠️ {warningThreshold}% threshold reached
        </p>
      )}
      {percentage >= 100 && (
        <p className="mt-1 text-xs text-red-600">
          ⚠️ License limit reached
        </p>
      )}
    </div>
  );
};

export default ProgressBar;
